import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { GitMerge, Loader2 } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type { MergeCatalogChange, MergeChange } from '@/api/endpoints/products'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Button } from '@/components/ui/button'
import { useProducts } from '@/features/products/hooks/use-products'
import { productsApi } from '@/api/endpoints/products'
import { getErrorMessage } from '@/shared/utils/error-codes'
import { formatCurrency } from '@/shared/utils/format-currency'
import { ApiError } from '@/api/types'

interface MergeProductDialogProps {
  /** The duplicate SKU being merged into a chosen primary. */
  sku: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

type Translate = (key: string, opts?: Record<string, unknown>) => string

/**
 * "Zusammenführen mit …" — picks the primary product, previews what the merge will change on the
 * primary's behalf (menu items, kiosk tiles, add-ons, prices), and only then calls the merge
 * endpoint (CHG-2026-09-duplicate-product-merge). The search offers only active webbill products
 * that are not merged themselves (server-side) and never the duplicate itself (client-side).
 */
export function MergeProductDialog({
  sku,
  open,
  onOpenChange,
}: MergeProductDialogProps) {
  const { t: tProducts } = useTranslation('products')
  const { t: tCommon } = useTranslation('common')
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSku, setSelectedSku] = useState<string | null>(null)

  const { data: productsData } = useProducts(
    {
      search: searchQuery || undefined,
      status: 'active',
      pageSize: 20,
      excludeMerged: true,
      webbillOnly: true,
    },
    { enabled: open },
  )

  const candidates = useMemo(
    () => (productsData?.items ?? []).filter((p) => p.sku !== sku),
    [productsData, sku],
  )

  const preview = useQuery({
    queryKey: ['products', sku, 'merge-preview', selectedSku],
    queryFn: () => productsApi.mergePreview(sku, selectedSku!),
    enabled: open && !!selectedSku,
    retry: false,
    gcTime: 0,
  })

  const mergeMutation = useMutation({
    mutationFn: (primarySku: string) => productsApi.merge(sku, primarySku),
    onSuccess: (res) => {
      toast.success(
        tProducts('merge.success', { sku, primary: res.primary.sku }),
      )
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['menus'] })
      queryClient.invalidateQueries({ queryKey: ['pos-layout'] })
      queryClient.invalidateQueries({ queryKey: ['product-price'] })
      handleOpenChange(false)
    },
    onError: (err) => {
      // A refusal is shown inline (see blockingError); only unexpected failures need a toast.
      if (!(err instanceof ApiError)) toast.error(tProducts('merge.failed'))
    },
  })

  const blockingError = [mergeMutation.error, preview.error].find(
    (e): e is ApiError => e instanceof ApiError,
  )

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setSearchQuery('')
      setSelectedSku(null)
      mergeMutation.reset()
    }
    onOpenChange(next)
  }

  const handleSelect = (primarySku: string) => {
    mergeMutation.reset()
    setSelectedSku(primarySku)
  }

  const changes = preview.data?.changes

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{tProducts('merge.dialogTitle')}</DialogTitle>
          <DialogDescription>
            {tProducts('merge.dialogDescription', { sku })}
          </DialogDescription>
        </DialogHeader>

        <Command shouldFilter={false} className="rounded-md border">
          <CommandInput
            placeholder={tProducts('merge.searchPlaceholder')}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList className="max-h-[220px]">
            <CommandEmpty>{tProducts('merge.empty')}</CommandEmpty>
            <CommandGroup>
              {candidates.map((product) => (
                <CommandItem
                  key={product.sku}
                  value={product.sku}
                  onSelect={() => handleSelect(product.sku)}
                  className={
                    selectedSku === product.sku ? 'bg-accent' : undefined
                  }
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{product.name}</span>
                    <span className="text-xs text-muted-foreground font-mono">
                      {product.sku}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>

        {blockingError ? (
          <div
            role="alert"
            className="rounded-md border border-destructive/50 bg-destructive/5 px-3 py-2 text-sm"
          >
            <p className="font-medium text-destructive">
              {tProducts('merge.blockedTitle')}
            </p>
            <p className="text-muted-foreground">
              {getErrorMessage(blockingError.status, blockingError.code)}
            </p>
          </div>
        ) : preview.isFetching ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {tProducts('merge.previewLoading')}
          </p>
        ) : changes ? (
          <div className="rounded-md border px-3 py-2 text-sm">
            {changes.length === 0 ? (
              <p className="text-muted-foreground">
                {tProducts('merge.previewNone')}
              </p>
            ) : (
              <>
                <p className="font-medium mb-1">
                  {tProducts('merge.previewTitle')}
                </p>
                <ul className="list-disc pl-4 space-y-1 max-h-[180px] overflow-y-auto">
                  {changes.map((change, idx) => (
                    <li key={idx}>{describeChange(change, tProducts)}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={mergeMutation.isPending}
          >
            {tCommon('cancel')}
          </Button>
          <Button
            type="button"
            disabled={
              !selectedSku ||
              !preview.isSuccess ||
              preview.isFetching ||
              mergeMutation.isPending
            }
            onClick={() => selectedSku && mergeMutation.mutate(selectedSku)}
          >
            <GitMerge className="h-4 w-4 mr-2" />
            {tProducts('merge.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function formatMenuDate(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString('de-AT', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/** Renders one entry of the merge preview as a readable line: where, then what happens there. */
export function describeChange(
  change: MergeChange | MergeCatalogChange,
  t: Translate,
): string {
  // CHG-2026-09-stock-usage-and-price-twins (D12): catalog-wide entries, no restaurant.
  if (change.type === 'stock_usage_part') {
    return t(
      change.primaryAlreadyThere
        ? 'merge.changeStockUsagePartAdded'
        : 'merge.changeStockUsagePart',
      { dishName: change.dishName, dishSku: change.dishSku },
    )
  }
  if (change.type === 'variant') {
    return t('merge.changeVariantMoved', {
      variantName: change.variantName,
      variantSku: change.variantSku,
    })
  }
  const restaurantName = change.restaurantName ?? String(change.restaurantId)
  const placement = (where: string, primaryAlreadyThere: boolean) =>
    `${where}: ${t(primaryAlreadyThere ? 'merge.changeDropped' : 'merge.changeReplaced')}`
  switch (change.type) {
    case 'master_menu':
      return placement(
        t('merge.changeMasterMenu', {
          restaurantName,
          sectionName: change.sectionName,
        }),
        change.primaryAlreadyThere,
      )
    case 'daily_menu':
      return placement(
        t('merge.changeDailyMenu', {
          restaurantName,
          sectionName: change.sectionName,
          menuDate: change.menuDate ? formatMenuDate(change.menuDate) : '',
        }),
        change.primaryAlreadyThere,
      )
    case 'kiosk_tile':
      return placement(
        t('merge.changeKioskTile', { restaurantName, page: change.page }),
        change.primaryAlreadyThere,
      )
    case 'checkout_addon':
      return placement(
        t('merge.changeCheckoutAddon', { restaurantName }),
        change.primaryAlreadyThere,
      )
    case 'price_copied':
      return t('merge.changePriceCopied', {
        restaurantName,
        price: formatCurrency(change.amountCents),
      })
    case 'assigned':
      return t('merge.changeAssigned', { restaurantName })
    case 'price_differs':
      return t('merge.changePriceDiffers', {
        restaurantName,
        primaryPrice: formatCurrency(change.primaryCents),
        duplicatePrice: formatCurrency(change.duplicateCents),
      })
  }
}
